import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Badge,
} from "@mui/material";
import {
  People,
  Business,
  TrendingUp,
  CheckCircle,
  PendingActions,
  Logout,
  Group,
  Edit,
  Delete,
  Launch,
  Inventory,
  FolderShared,
  FormatListBulleted,
  NoteAlt,
  FolderCopy,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import auth from "../services/auth";
import {
  createCompanyAccess,
  deleteCompanyAccess,
  editCompanyAccess,
  getClients,
  getUnreadFileCounts,
  getCompanyAccesses,
  getComunicados,
} from "../services/source";

const DashboardScreen = () => {
  const navigate = useNavigate();
  const user = auth.getCurrentUser();

  const userId = user?.data?.companyId || user?.data?.id;
  const requesterId = user?.data?.id;
  const isOwnerUser = user?.data?.companyRole
    ? user.data.companyRole === "owner"
    : requesterId === userId;
  const companyName = user?.data?.empresa || "utilizador";

  const [clients, setClients] = useState([]);
  const [accesses, setAccesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAccessModal, setOpenAccessModal] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState("");
  const [accessSuccess, setAccessSuccess] = useState("");
  const [accessForm, setAccessForm] = useState({
    nome: "",
    email: "",
    password: "",
  });
  const [openEditAccessModal, setOpenEditAccessModal] = useState(false);
  const [openDeleteAccessModal, setOpenDeleteAccessModal] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState(null);
  const [editAccessForm, setEditAccessForm] = useState({
    nome: "",
    email: "",
    password: "",
    nif: "",
    telemovel: "",
    whatsapp: "",
    morada: "",
    pais: "",
    cidade: "",
  });
  const [comunicados, setComunicados] = useState([]);
  const [comunicadosLoading, setComunicadosLoading] = useState(true);
  const [unreadInternalArchive, setUnreadInternalArchive] = useState(0);
  const [unreadDailyReports] = useState(0);

  const refreshAccesses = async () => {
    const accessData = await getCompanyAccesses(userId);
    setAccesses(Array.isArray(accessData) ? accessData : []);
  };

  useEffect(() => {
    let active = true;

    const loadComunicados = async () => {
      try {
        const data = await getComunicados();
        if (active) {
          setComunicados(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Error loading comunicados:", err);
        if (active) {
          setComunicados([]);
        }
      } finally {
        if (active) {
          setComunicadosLoading(false);
        }
      }
    };

    loadComunicados();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!userId) return;
      try {
        const [clientData, accessData] = await Promise.all([
          getClients(userId),
          getCompanyAccesses(userId),
        ]);
        setClients(Array.isArray(clientData) ? clientData : []);
        setAccesses(Array.isArray(accessData) ? accessData : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userId]);

  useEffect(() => {
    let active = true;

    const loadUnreadInternalArchive = async () => {
      if (!userId || !requesterId || !user?.data?.type) {
        if (active) {
          setUnreadInternalArchive(0);
        }
        return;
      }

      const res = await getUnreadFileCounts({
        user_id: requesterId,
        user_type: user.data.type,
        company_id: userId,
      });

      if (active && res?.success) {
        setUnreadInternalArchive(Number(res?.data?.arquivo_interno || 0));
      }
    };

    loadUnreadInternalArchive();

    return () => {
      active = false;
    };
  }, [requesterId, user?.data?.type, userId]);

  const stats = useMemo(() => {
    const total = clients.length;
    const active = clients.filter((c) => c.status === "Ativo").length;
    const inactive = clients.filter((c) => c.status !== "Ativo").length;
    const completion = total ? Math.round((active / total) * 100) : 0;

    return {
      total,
      active,
      inactive,
      completion,
      accesses: accesses.length,
    };
  }, [accesses.length, clients]);

  const recentClients = [...clients]
    .sort((a, b) => (b.nome || "").localeCompare(a.nome || ""))
    .slice(0, 5);

  const handleAccessChange = (field) => (event) => {
    setAccessForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCreateAccess = async () => {
    if (!isOwnerUser) {
      setAccessError("Apenas o owner pode gerir acessos.");
      return;
    }

    setAccessError("");
    setAccessSuccess("");

    if (!accessForm.nome || !accessForm.email || !accessForm.password) {
      setAccessError("Preencha nome, email e senha.");
      return;
    }

    if (accessForm.password.length < 6) {
      setAccessError("A senha deve ter pelo menos 6 caracteres.");
      return;
    }

    try {
      setAccessLoading(true);
      await createCompanyAccess({
        companyId: userId,
        nome: accessForm.nome,
        email: accessForm.email,
        password: accessForm.password,
      });

      await refreshAccesses();
      setAccessSuccess("Acesso criado com sucesso.");
      setAccessForm({ nome: "", email: "", password: "" });
      setOpenAccessModal(false);
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setAccessError(apiMessage || "Não foi possível criar o acesso.");
    } finally {
      setAccessLoading(false);
    }
  };

  const handleRequestDeleteAccess = (access) => {
    if (!isOwnerUser) {
      setAccessError("Apenas o owner pode gerir acessos.");
      return;
    }

    if (access?.role === "owner") {
      setAccessError("O owner não pode ser removido.");
      return;
    }

    setAccessError("");
    setAccessSuccess("");
    setSelectedAccess(access);
    setOpenDeleteAccessModal(true);
  };

  const handleDeleteAccess = async () => {
    if (!selectedAccess?.id) {
      setAccessError("Acesso inválido.");
      return;
    }

    try {
      setAccessLoading(true);
      await deleteCompanyAccess({
        companyId: userId,
        requesterId,
        accessId: selectedAccess.id,
      });

      await refreshAccesses();
      setAccessSuccess("Acesso removido com sucesso.");
      setOpenDeleteAccessModal(false);
      setSelectedAccess(null);
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setAccessError(apiMessage || "Não foi possível remover o acesso.");
    } finally {
      setAccessLoading(false);
    }
  };

  const handleOpenEditAccess = (access) => {
    if (!isOwnerUser) {
      setAccessError("Apenas o owner pode gerir acessos.");
      return;
    }

    setAccessError("");
    setAccessSuccess("");
    setSelectedAccess(access);
    setEditAccessForm({
      nome: access?.nome || "",
      email: access?.email || "",
      password: "",
      nif: access?.nif || "",
      telemovel: access?.telemovel || "",
      whatsapp: access?.whatsapp || "",
      morada: access?.morada || "",
      pais: access?.pais || "",
      cidade: access?.cidade || "",
    });
    setOpenEditAccessModal(true);
  };

  const handleEditAccessChange = (field) => (event) => {
    setEditAccessForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEditAccess = async () => {
    setAccessError("");
    setAccessSuccess("");

    if (!selectedAccess?.id) {
      setAccessError("Acesso inválido.");
      return;
    }

    if (!editAccessForm.nome || !editAccessForm.email) {
      setAccessError("Preencha nome e email.");
      return;
    }

    if (selectedAccess?.role === "owner") {
      if (
        !editAccessForm.nif ||
        !editAccessForm.telemovel ||
        !editAccessForm.whatsapp ||
        !editAccessForm.morada ||
        !editAccessForm.pais ||
        !editAccessForm.cidade
      ) {
        setAccessError(
          "Para o owner, preencha nif, telemovel, whatsapp, morada, pais e cidade.",
        );
        return;
      }
    }

    if (
      editAccessForm.password &&
      editAccessForm.password.length > 0 &&
      editAccessForm.password.length < 6
    ) {
      setAccessError("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }

    try {
      setAccessLoading(true);
      await editCompanyAccess({
        companyId: userId,
        requesterId,
        accessId: selectedAccess.id,
        nome: editAccessForm.nome,
        email: editAccessForm.email,
        password: editAccessForm.password,
        nif: editAccessForm.nif,
        telemovel: editAccessForm.telemovel,
        whatsapp: editAccessForm.whatsapp,
        morada: editAccessForm.morada,
        pais: editAccessForm.pais,
        cidade: editAccessForm.cidade,
      });

      await refreshAccesses();
      setAccessSuccess("Acesso atualizado com sucesso.");
      setOpenEditAccessModal(false);
      setSelectedAccess(null);
      setEditAccessForm({
        nome: "",
        email: "",
        password: "",
        nif: "",
        telemovel: "",
        whatsapp: "",
        morada: "",
        pais: "",
        cidade: "",
      });
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setAccessError(apiMessage || "Não foi possível atualizar o acesso.");
    } finally {
      setAccessLoading(false);
    }
  };

  return (
    <Box
      sx={{ p: { xs: 2, md: 4 }, background: "#f7f5fb", minHeight: "100vh" }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          mb: 3,
          background: "linear-gradient(90deg, #5b2c72 0%, #7b1fa2 100%)",
          color: "white",
          overflow: "hidden",
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1.5, sm: 2 },
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.12)",
                width: { xs: 44, sm: 56 },
                height: { xs: 44, sm: 56 },
                border: "1px solid rgba(255,255,255,0.18)",
              }}
            >
              <Business sx={{ fontSize: { xs: 24, sm: 30 } }} />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                fontWeight="bold"
                color="white"
                sx={{
                  fontSize: { xs: "1.3rem", sm: "1.8rem", md: "2.125rem" },
                  lineHeight: 1.2,
                  wordBreak: "break-word",
                }}
              >
                {companyName}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Bem-vindo(a)
              </Typography>
            </Box>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{
              width: { xs: "100%", md: "auto" },
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            {isOwnerUser && (
              <Chip
                label="Novo acesso"
                color="default"
                onClick={() => {
                  setAccessError("");
                  setAccessSuccess("");
                  setOpenAccessModal(true);
                }}
                size="small"
                sx={{
                  cursor: "pointer",
                  backgroundColor: "rgba(255,255,255,0.14)",
                  color: "white",
                  fontWeight: 600,
                }}
              />
            )}
            <Chip
              label="Clientes"
              color="default"
              icon={<Group color="inherit" />}
              onClick={() => navigate("/clientTable")}
              size="small"
              sx={{
                cursor: "pointer",
                backgroundColor: "rgba(255,255,255,0.14)",
                color: "white",
                fontWeight: 600,
              }}
            />
            <Chip
              label="Sair"
              color="default"
              onClick={() => {
                auth.logout();
                navigate("/");
              }}
              icon={<Logout color="inherit" />}
              size="small"
              sx={{
                cursor: "pointer",
                backgroundColor: "rgba(255,255,255,0.14)",
                color: "white",
                fontWeight: 600,
              }}
            />
          </Stack>
        </CardContent>
      </Card>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<People sx={{ fontSize: 30 }} />}
            label="Total de clientes"
            value={stats.total}
            color="#7b1fa2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CheckCircle sx={{ fontSize: 30 }} />}
            label="Ativos"
            value={stats.active}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PendingActions sx={{ fontSize: 30 }} />}
            label="Inativos"
            value={stats.inactive}
            color="#d32f2f"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingUp sx={{ fontSize: 30 }} />}
            label="Taxa de atividade"
            value={`${stats.completion}%`}
            color="#0288d1"
          />
        </Grid>
      </Grid>
      {/* Progresso geral */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 4, height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Progresso geral
              </Typography>
              <LinearProgress
                variant="determinate"
                value={stats.completion}
                sx={{ height: 10, borderRadius: 5, mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {stats.active} clientes ativos de {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 4, height: "100%" }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center">
                <FolderShared color="secondary" />
                <Typography variant="h6" fontWeight="bold">
                  Resumo rápido
                </Typography>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1.5}>
                <MetricRow label="Clientes" value={stats.total} />
                <MetricRow label="Ativos" value={stats.active} />
                <MetricRow label="Inativos" value={stats.inactive} />
                <MetricRow label="Acessos" value={stats.accesses} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Acessos da empresa */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 4 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Acessos da empresa
              </Typography>
              {accessError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {accessError}
                </Alert>
              )}
              {accessSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {accessSuccess}
                </Alert>
              )}
              <List>
                {loading
                  ? Array.from({ length: 3 }).map((_, idx) => (
                      <ListItem key={idx}>
                        <ListItemAvatar>
                          <Avatar />
                        </ListItemAvatar>
                        <ListItemText primary="Carregando..." secondary="" />
                      </ListItem>
                    ))
                  : accesses.map((access) => (
                      <ListItem key={access.id} disablePadding>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: "#5b2c72" }}>
                            {access.nome?.charAt(0) || "U"}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={access.nome}
                          secondary={access.email}
                        />
                        <Chip
                          label={access.role === "owner" ? "Owner" : "Acesso"}
                          color={
                            access.role === "owner" ? "secondary" : "default"
                          }
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {isOwnerUser && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditAccess(access)}
                              sx={{ mr: 0.5, color: "success.main" }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            {access.role !== "owner" && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleRequestDeleteAccess(access)
                                }
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        )}
                      </ListItem>
                    ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Ações rápidas */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 4 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Ações rápidas
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                }}
              >
                <QuickActionCard
                  icon={<Business color="primary" />}
                  title="Gerir clientes"
                  subtitle="Verificar e editar clientes"
                  onClick={() => navigate("/clientTable")}
                />
                {isOwnerUser && (
                  <QuickActionCard
                    icon={<Group color="primary" />}
                    title="Gerir acessos"
                    subtitle="Criar mais logins para a sua empresa"
                    onClick={() => setOpenAccessModal(true)}
                  />
                )}

                <QuickActionCard
                  icon={<FormatListBulleted color="primary" />}
                  title="Ver logs"
                  subtitle="Acompanhar atividades e alterações"
                  onClick={() => navigate("/logs")}
                />
                <QuickActionCard
                  icon={<Inventory color="primary" />}
                  title="Arquivo interno"
                  subtitle="Abrir o arquivo interno da empresa"
                  onClick={() => navigate("/internalArchive")}
                  badgeCount={unreadInternalArchive}
                />
                <QuickActionCard
                  icon={<NoteAlt color="primary" />}
                  title="Partes Diarias"
                  subtitle="Partes diarias da empresa"
                  onClick={() => navigate("/dailyReports")}
                  badgeCount={unreadDailyReports}
                />
                <QuickActionCard
                  icon={<FolderCopy color="primary" />}
                  title="Formularios e modelos"
                  subtitle="Formulários e modelos da empresa"
                  onClick={() => navigate("/formsAndTemplates")}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 4 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Clientes recentes
              </Typography>
              <List>
                {loading
                  ? Array.from({ length: 4 }).map((_, idx) => (
                      <ListItem key={idx}>
                        <ListItemAvatar>
                          <Avatar />
                        </ListItemAvatar>
                        <ListItemText primary="Carregando..." secondary="" />
                      </ListItem>
                    ))
                  : recentClients.map((client) => (
                      <ListItem key={client.id} disablePadding>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: "#9c27b0" }}>
                            {client.nome?.charAt(0) || "C"}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={client.nome}
                          secondary={client.email}
                        />
                        <Chip
                          label={client.status || "Ativo"}
                          color={
                            client.status === "Ativo" ? "success" : "error"
                          }
                          size="small"
                        />
                      </ListItem>
                    ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Comunicados AGT */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 6,
              border: "1px solid #ff9c33",
              background: "linear-gradient(135deg, #e9e9e9 0%, #e0eaff 100%)",
            }}
          >
            <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Comunicados AGT
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Conteúdo obtido do Portal do Contribuinte.
              </Typography>
              <List>
                {comunicadosLoading &&
                  Array.from({ length: 3 }).map((_, idx) => (
                    <ListItem key={idx} disablePadding sx={{ py: 1 }}>
                      <ListItemText primary="Carregando comunicados..." />
                    </ListItem>
                  ))}

                {!comunicadosLoading && comunicados.length === 0 && (
                  <ListItem disablePadding sx={{ py: 1 }}>
                    <ListItemText primary="Sem comunicados disponíveis no momento." />
                  </ListItem>
                )}

                {!comunicadosLoading &&
                  comunicados.map((item, idx) => (
                    <ListItem
                      key={`${item.url || "comunicado"}-${idx}`}
                      disablePadding
                      divider
                      sx={{ py: 1.2, alignItems: "stretch" }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "stretch", sm: "center" },
                          justifyContent: "space-between",
                          gap: { xs: 1, sm: 2 },
                        }}
                      >
                        <ListItemText
                          primary={item.title || "COMUNICADO"}
                          secondary={item.date || "Sem data"}
                          primaryTypographyProps={{
                            sx: {
                              fontSize: { xs: "0.95rem", sm: "1rem" },
                              lineHeight: 1.35,
                              wordBreak: "break-word",
                            },
                          }}
                          secondaryTypographyProps={{
                            sx: {
                              mt: 0.4,
                              fontSize: { xs: "0.78rem", sm: "0.82rem" },
                            },
                          }}
                          sx={{ my: 0, mr: { sm: 1 } }}
                        />

                        {item.url && (
                          <Button
                            size="small"
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<Launch fontSize="small" />}
                            sx={{
                              alignSelf: { xs: "flex-start", sm: "center" },
                              minWidth: { xs: 96, sm: "auto" },
                            }}
                          >
                            Abrir
                          </Button>
                        )}
                      </Box>
                    </ListItem>
                  ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* ############## */}
      <Dialog
        open={isOwnerUser && openAccessModal}
        onClose={() => !accessLoading && setOpenAccessModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Criar novo acesso</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {accessError && <Alert severity="error">{accessError}</Alert>}
            {accessSuccess && <Alert severity="success">{accessSuccess}</Alert>}
            <TextField
              label="Nome"
              value={accessForm.nome}
              onChange={handleAccessChange("nome")}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={accessForm.email}
              onChange={handleAccessChange("email")}
              fullWidth
            />
            <TextField
              label="Senha"
              type="password"
              value={accessForm.password}
              onChange={handleAccessChange("password")}
              fullWidth
              helperText="Mínimo de 6 caracteres"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenAccessModal(false)}
            disabled={accessLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateAccess}
            variant="contained"
            disabled={accessLoading}
            sx={{
              color: "white",
              backgroundColor: "#7b1fa2",
              "&:hover": { backgroundColor: "#5b2c72" },
            }}
          >
            {accessLoading ? "A criar..." : "Criar acesso"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isOwnerUser && openEditAccessModal}
        onClose={() => !accessLoading && setOpenEditAccessModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar acesso</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome"
              value={editAccessForm.nome}
              onChange={handleEditAccessChange("nome")}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={editAccessForm.email}
              onChange={handleEditAccessChange("email")}
              fullWidth
            />
            <TextField
              label="Nova senha (opcional)"
              type="password"
              value={editAccessForm.password}
              onChange={handleEditAccessChange("password")}
              fullWidth
              helperText="Deixe vazio para manter a senha atual"
            />
            {selectedAccess?.role === "owner" && (
              <>
                <TextField
                  label="NIF"
                  value={editAccessForm.nif}
                  onChange={handleEditAccessChange("nif")}
                  fullWidth
                />
                <TextField
                  label="Telemovel"
                  value={editAccessForm.telemovel}
                  onChange={handleEditAccessChange("telemovel")}
                  fullWidth
                />
                <TextField
                  label="WhatsApp"
                  value={editAccessForm.whatsapp}
                  onChange={handleEditAccessChange("whatsapp")}
                  fullWidth
                />
                <TextField
                  label="Morada"
                  value={editAccessForm.morada}
                  onChange={handleEditAccessChange("morada")}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <TextField
                  label="País"
                  value={editAccessForm.pais}
                  onChange={handleEditAccessChange("pais")}
                  fullWidth
                />
                <TextField
                  label="Cidade"
                  value={editAccessForm.cidade}
                  onChange={handleEditAccessChange("cidade")}
                  fullWidth
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenEditAccessModal(false)}
            disabled={accessLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEditAccess}
            variant="contained"
            disabled={accessLoading}
          >
            {accessLoading ? "A guardar..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isOwnerUser && openDeleteAccessModal}
        onClose={() => !accessLoading && setOpenDeleteAccessModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Remover acesso</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Tem certeza que deseja remover o acesso de {selectedAccess?.nome}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteAccessModal(false)}
            disabled={accessLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteAccess}
            color="error"
            variant="contained"
            disabled={accessLoading}
          >
            {accessLoading ? "A remover..." : "Remover"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const StatCard = ({ icon, label, value, color }) => (
  <Card sx={{ borderRadius: 3, boxShadow: "0 8px 18px rgba(0,0,0,0.08)" }}>
    <CardContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>{icon}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

const MetricRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight="bold">
      {value}
    </Typography>
  </Box>
);

const QuickActionCard = ({
  icon,
  title,
  subtitle,
  onClick,
  badgeCount = 0,
}) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: "pointer",
      borderRadius: 3,
      p: 1,
      height: "100%",
      transition: "0.2s",
      "&:hover": { backgroundColor: "#f4eefc" },
    }}
  >
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Avatar sx={{ bgcolor: "#efe5ff" }}>{icon}</Avatar>
      <Box>
        <Badge
          color="error"
          badgeContent={badgeCount}
          invisible={!badgeCount}
          sx={{ "& .MuiBadge-badge": { right: -18 } }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {title}
          </Typography>
        </Badge>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

export default DashboardScreen;
